import React from 'react';
import { ArrowLeft, Shield, Eye, Lock, FileText, Globe, Mail, Phone } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export default function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="fixed top-6 left-6 z-50 glass-morphism text-pink-400 px-6 py-3 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="pt-32 pb-20 relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h1 className="font-poppins font-black text-4xl lg:text-6xl text-white mb-4">
                Privacy Policy
              </h1>
              <p className="font-inter text-xl text-slate-300">
                Effective Date: January 1, 2025
              </p>
            </div>

            {/* Content */}
            <div className="premium-card rounded-3xl p-8 lg:p-12">
              <div className="prose prose-lg prose-invert max-w-none">
                <div className="space-y-8">
                  {/* Introduction */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Eye className="h-6 w-6 text-cyan-400" />
                      <span>Introduction</span>
                    </h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      Flavors The Bold Pte Ltd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our mobile application, or purchase our products. This policy complies with the Personal Data Protection Act 2012 (PDPA) of Singapore.
                    </p>
                  </section>

                  {/* Information We Collect */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-green-400" />
                      <span>Information We Collect</span>
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Personal Information</h3>
                        <ul className="font-inter text-slate-300 space-y-2 ml-4">
                          <li>• Name, email address, phone number</li>
                          <li>• Billing and shipping addresses</li>
                          <li>• Payment information (processed securely by third-party providers)</li>
                          <li>• Account credentials and preferences</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-lg text-pink-400 mb-2">Usage Information</h3>
                        <ul className="font-inter text-slate-300 space-y-2 ml-4">
                          <li>• DrizzID™ quiz responses and taste preferences</li>
                          <li>• Website and app usage analytics</li>
                          <li>• Device information and IP addresses</li>
                          <li>• Cookies and similar tracking technologies</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* How We Use Your Information */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Lock className="h-6 w-6 text-purple-400" />
                      <span>How We Use Your Information</span>
                    </h2>
                    <ul className="font-inter text-slate-300 space-y-3 ml-4">
                      <li>• Process and fulfill your orders</li>
                      <li>• Provide personalized product recommendations through DrizzID™</li>
                      <li>• Send order confirmations, shipping updates, and customer service communications</li>
                      <li>• Improve our products, services, and user experience</li>
                      <li>• Send marketing communications (with your consent)</li>
                      <li>• Comply with legal obligations and prevent fraud</li>
                    </ul>
                  </section>

                  {/* Information Sharing */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Information Sharing and Disclosure</h2>
                    <p className="font-inter text-slate-300 leading-relaxed mb-4">
                      We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                    </p>
                    <ul className="font-inter text-slate-300 space-y-2 ml-4">
                      <li>• With service providers who assist in our operations (shipping, payment processing, analytics)</li>
                      <li>• When required by law or to protect our rights and safety</li>
                      <li>• In connection with a business transfer or merger</li>
                      <li>• With your explicit consent</li>
                    </ul>
                  </section>

                  {/* Data Security */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Data Security</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                    </p>
                  </section>

                  {/* Your Rights */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Your Rights Under Singapore PDPA</h2>
                    <ul className="font-inter text-slate-300 space-y-2 ml-4">
                      <li>• Right to access your personal data</li>
                      <li>• Right to correct inaccurate personal data</li>
                      <li>• Right to withdraw consent for marketing communications</li>
                      <li>• Right to request deletion of your personal data (subject to legal requirements)</li>
                    </ul>
                  </section>

                  {/* Cookies */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Cookies and Tracking</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser preferences.
                    </p>
                  </section>

                  {/* International Transfers */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">International Data Transfers</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      Your personal data may be transferred to and processed in countries outside Singapore. We ensure appropriate safeguards are in place to protect your data in accordance with Singapore's PDPA requirements.
                    </p>
                  </section>

                  {/* Contact Information */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4 flex items-center space-x-3">
                      <Mail className="h-6 w-6 text-cyan-400" />
                      <span>Contact Us</span>
                    </h2>
                    <div className="bg-black/30 rounded-xl p-6">
                      <p className="font-inter text-slate-300 leading-relaxed mb-4">
                        If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
                      </p>
                      <div className="space-y-2 font-inter text-slate-300">
                        <p><strong>Flavors The Bold Pte Ltd</strong></p>
                        <p>Email: privacy@undressing.com</p>
                        <p>Address: [Singapore Business Address]</p>
                        <p>Data Protection Officer: dpo@undressing.com</p>
                      </div>
                    </div>
                  </section>

                  {/* Updates */}
                  <section>
                    <h2 className="font-poppins font-bold text-2xl text-white mb-4">Policy Updates</h2>
                    <p className="font-inter text-slate-300 leading-relaxed">
                      We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the effective date.
                    </p>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}